import React, { useState } from "react";
import FileUpload from "./FileUpload";
import { useExtractClassSchedule } from "../../../apis/aiApi.hooks";
import { ClassScheduleExtractorResponse } from "../../../apis/aiApi";

interface ScheduleScreenshotUploadProps {
  onImageUploaded: (base64: string, filename: string) => void;
  onExtractionComplete?: (
    extractedData: ClassScheduleExtractorResponse & { success: boolean },
  ) => void;
  disabled?: boolean;
  currentFile?: { name: string; base64: string } | null;
}

const ScheduleScreenshotUpload: React.FC<ScheduleScreenshotUploadProps> = ({
  onImageUploaded,
  onExtractionComplete,
  disabled = false,
  currentFile = null,
}) => {
  const [extractionError, setExtractionError] = useState<string | null>(null);

  const { mutate: extractClassSchedule, isLoading: isExtracting } =
    useExtractClassSchedule();

  const handleFileUpload = async (base64: string, filename: string) => {
    onImageUploaded(base64, filename);
    setExtractionError(null);

    if (base64 && onExtractionComplete) {
      extractClassSchedule(
        {
          type: "image",
          content: base64,
        },
        {
          onSuccess: (data) => {
            console.log("Schedule extraction successful:", data);
            onExtractionComplete({
              ...data,
              success: true,
            });
          },
          onError: (error) => {
            console.error("Schedule extraction failed:", error);
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Failed to extract schedule from image";
            setExtractionError(errorMessage);

            onExtractionComplete({
              classes: [],
              success: false,
            });
          },
        },
      );
    }
  };

  const handleRemoveFile = () => {
    onImageUploaded("", "");
    setExtractionError(null);
  };

  return (
    <div className="tw-space-y-4">
      <FileUpload
        title="Or, use your schedule"
        uploadText="Upload Schedule Screenshot"
        onFileUploaded={handleFileUpload}
        acceptedTypes="image/jpeg,image/jpg,image/png,image/heic,image/heif"
        maxSizeMB={10}
        currentFile={currentFile}
        disabled={disabled || isExtracting}
        showPreview={true}
        onFileRemoved={handleRemoveFile}
      />

      {/* Extraction Loading State */}
      {isExtracting && (
        <div className="tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg">
          <div className="tw-flex tw-items-center tw-space-x-3">
            <div className="tw-w-5 tw-h-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600 tw-rounded-full tw-animate-spin"></div>
            <div>
              <p className="tw-text-sm tw-font-medium tw-text-blue-900">
                Analyzing your schedule...
              </p>
              <p className="tw-text-xs tw-text-blue-700">
                This may take a few seconds
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Extraction Error State */}
      {extractionError && !isExtracting && (
        <div className="tw-p-4 tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg">
          <div className="tw-flex tw-items-start tw-space-x-3">
            <div className="tw-w-5 tw-h-5 tw-bg-orange-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-mt-0.5">
              <span className="tw-text-orange-600 tw-text-xs tw-font-bold">
                !
              </span>
            </div>
            <div>
              <p className="tw-text-sm tw-font-medium tw-text-orange-900">
                Couldn&apos;t extract schedule automatically
              </p>
              <p className="tw-text-xs tw-text-orange-700 tw-mt-1">
                {extractionError}. You can still manually select class types
                below.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleScreenshotUpload;
